import React from 'react'
import {
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'
import { CampaignSetupFormValues, CampaignObjective } from '../types'
import StepNavigation from './StepNavigation'

const IS_PRODUCTION = process.env.REACT_APP_ENV === 'production'

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}))

interface Props {
  values: CampaignSetupFormValues
  onChange: (field: keyof CampaignSetupFormValues, value: unknown) => void
  onNext: () => void
  onBack: () => void
  readOnly?: boolean
}

const OBJECTIVES: { value: CampaignObjective; label: string }[] = [
  { value: 'LEAD_GENERATION', label: 'Lead Generation' },
  { value: 'OUTCOME_TRAFFIC', label: 'Traffic' },
]

const CampaignStep: React.FC<Props> = ({ values, onChange, onNext, onBack, readOnly }) => {
  return (
    <Box>
      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Campaign
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Campaign Name *"
                value={values.campaignName}
                onChange={(e) => onChange('campaignName', e.target.value)}
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Campaign Objective</InputLabel>
                <Select
                  value={values.campaignObjective}
                  label="Campaign Objective"
                  onChange={(e) => onChange('campaignObjective', e.target.value)}
                >
                  {OBJECTIVES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Tooltip
                title={
                  !IS_PRODUCTION
                    ? 'Status is locked to Paused in non-production environments to avoid Facebook costs.'
                    : ''
                }
                placement="top"
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value="PAUSED" label="Status" disabled>
                    <MenuItem value="PAUSED">Paused (PAUSED)</MenuItem>
                    {IS_PRODUCTION && <MenuItem value="ACTIVE">Active (ACTIVE)</MenuItem>}
                  </Select>
                </FormControl>
              </Tooltip>
              {!IS_PRODUCTION && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Locked to Paused (non-production)
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <Tooltip
                title="Special Ad Category is fixed to Employment for job advertising campaigns."
                placement="top"
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Special Ad Categories</InputLabel>
                  <Select value="EMPLOYMENT" label="Special Ad Categories" disabled>
                    <MenuItem value="EMPLOYMENT">Employment (EMPLOYMENT)</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Fixed to Employment for job campaigns
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </SectionCard>

      <StepNavigation onBack={onBack} onNext={onNext} />
    </Box>
  )
}

export default CampaignStep

